import { jsx as _jsx } from "react/jsx-runtime";
import { EfficientListLoader } from "@faire/retailer-visitor-shared/components/EfficientListLoader/EfficientListLoader";
import { withFallback } from "@faire/retailer-visitor-shared/components/GhostLoading/helpers";
import { LeanBrandTileRenderer } from "@faire/retailer-visitor-shared/components/LeanBrandTileRenderer/LeanBrandTileRenderer";
import { followBrand, trackBoardsEvent, unFollowBrand } from "@faire/retailer-visitor-shared/features/RetailerBoards/utils";
import { SignUpElement } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { trackBrandsBrandTileClick } from "@faire/web-api--source/events/brands/click/brandTile";
import { trackCategoriesBrandBatchView } from "@faire/web-api--source/events/categories/view/brandBatch";
import isChromatic from "chromatic/isChromatic";
import { observer } from "mobx-react";
import * as React from "react";
import { useMemo } from "react";
import styled, { css } from "styled-components";
var BRAND_TILE_CONTAINER_CONTEXT = "CATEGORY_BRAND_RESULTS_GRID";
var ROW_PER_BATCH = 2;
var BrandGridBase = function(param) {
    var searchResults = param.searchResults, requestId = param.requestId, title = param.title, pageType = param.pageType, category = param.category, filterKeys = param.filterKeys, isLoading = param.isLoading, scrollRef = param.scrollRef, isScrollingToTop = param.isScrollingToTop, onFirstBatchVisible = param.onFirstBatchVisible, overrideItemsPerRow = param.itemsPerRow;
    var isSSR = useIsSSR();
    var onBrandBatchVisible = function(items, tileBatchIndex) {
        if (requestId) {
            trackCategoriesBrandBatchView(requestId, items.map(function(i) {
                var _i_brand;
                return i === null || i === void 0 ? void 0 : (_i_brand = i.brand) === null || _i_brand === void 0 ? void 0 : _i_brand.token;
            }).filter(isNotUndefined).join(" "));
        }
        if (tileBatchIndex === 0) {
            onFirstBatchVisible === null || onFirstBatchVisible === void 0 ? void 0 : onFirstBatchVisible();
        }
    };
    var postFavoriteBrand = function(brandToken, brandName) {
        followBrand(brandToken, brandName);
        trackBoardsEvent("brand/".concat(brandToken, "/follow/category"));
    };
    var deleteFavoriteBrand = function(brandToken) {
        unFollowBrand(brandToken);
        trackBoardsEvent("brand/".concat(brandToken, "/unfollow/category"));
    };
    var handleBrandClick = function(brandToken) {
        if (brandToken && category) {
            var eventContextString = "".concat(BRAND_TILE_CONTAINER_CONTEXT, "|").concat(category.mostSpecificPathIdentifier);
            trackBrandsBrandTileClick(eventContextString, requestId !== null && requestId !== void 0 ? requestId : "", brandToken);
        }
    };
    var renderTile = function(tileData) {
        var _tileData_brand;
        return /*#__PURE__*/ _jsx(TileWrapper, {
            "data-test-id": "maker-tile-wrapper",
            children: /*#__PURE__*/ _jsx(LeanBrandTileRenderer, {
                title: title,
                pageType: pageType,
                brandTile: tileData,
                categoryUrlIdList: category === null || category === void 0 ? void 0 : category.urlIdStrings,
                filterKeys: filterKeys,
                favoriteBrand: postFavoriteBrand,
                unFavoriteBrand: deleteFavoriteBrand,
                onClick: handleBrandClick,
                signUpTracking: {
                    element: SignUpElement.PRODUCT_GRID,
                    sourceId: "category_brand_grid"
                }
            })
        }, tileData === null || tileData === void 0 ? void 0 : (_tileData_brand = tileData.brand) === null || _tileData_brand === void 0 ? void 0 : _tileData_brand.token);
    };
    var renderPlaceHolder = function(placeholderItems, tileBatchIndex) {
        return /*#__PURE__*/ _jsx(React.Fragment, {
            children: placeholderItems === null || placeholderItems === void 0 ? void 0 : placeholderItems.map(function(_, index) {
                return /*#__PURE__*/ _jsx(React.Fragment, {
                    children: renderTile(undefined)
                }, "placeholder-".concat(index));
            })
        }, "placeholder-batch-".concat(tileBatchIndex));
    };
    var itemsPerRow = useResponsiveValue(overrideItemsPerRow !== null && overrideItemsPerRow !== void 0 ? overrideItemsPerRow : {
        mobileAndAbove: 2,
        tabletAndAbove: 4
    });
    var items = useMemo(function() {
        if (searchResults.length > 0) {
            return searchResults;
        }
        return withFallback(searchResults, 20);
    }, [
        searchResults
    ]);
    var testIdAttribute = isLoading ? "marketplace-brand-list-loading" : "marketplace-brand-list";
    return /*#__PURE__*/ _jsx(Wrapper, {
        "data-test-id": testIdAttribute,
        $columns: itemsPerRow,
        ref: scrollRef,
        children: /*#__PURE__*/ _jsx(EfficientListLoader, {
            data: items,
            batchSize: itemsPerRow * ROW_PER_BATCH,
            forceRender: isSSR || isChromatic(),
            // Remember to update the placeholder if the tile changes
            placeholderFn: renderPlaceHolder,
            renderComponent: renderTile,
            onBatchVisible: onBrandBatchVisible,
            hashKey: requestId,
            renderPlaceholderForNonFirstBatch: isScrollingToTop,
            visibilityWrapperDisplayProperty: "unset"
        })
    });
};
export var CategoryPageBrandGrid = observer(BrandGridBase);
var Wrapper = styled.div.withConfig({
    componentId: "sc-a03885d8-0"
})([
    "display:grid;align-self:stretch;grid-template-columns:[start] repeat(",
    ",minmax(0,1fr)) [end];margin:0 -4px;",
    "{margin:0 -8px;}"
], function(props) {
    return props.$columns;
}, responsiveMediaQueries.tabletAndAbove);
var TileWrapperStyles = css([
    "width:100%;padding:8px 4px;",
    "{padding:8px;}"
], responsiveMediaQueries.tabletAndAbove);
var TileWrapper = styled.div.withConfig({
    componentId: "sc-a03885d8-1"
})([
    "",
    ""
], TileWrapperStyles);
